import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { ContentPageLayout } from '../components/Shared';
import {
  createFormation,
  listFormations,
  type TrainingProgramSummary,
} from '../services/formationsAdminApi';

const FormationsListPage: React.FC = () => {
  const { t } = useTranslation();
  const { navigateWithLang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [programs, setPrograms] = useState<TrainingProgramSummary[]>([]);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newTitle, setNewTitle] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listFormations(
        search.trim() ? { search: search.trim() } : {},
      );
      setPrograms(res.data?.programs || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load formations');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!newCode.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await createFormation({
        code: newCode.trim(),
        locales: {
          en: { title: newTitle.trim() || newCode.trim() },
          fr: { title: newTitle.trim() || newCode.trim() },
        },
      });
      const id = res.data?.program?._id;
      if (id) navigateWithLang(`/formations/${id}`);
      else await load();
      setNewCode('');
      setNewTitle('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create formation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <ContentPageLayout>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.list.title', { defaultValue: 'Formations' })}</Typography>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            label={t('formations.list.search', { defaultValue: 'Search' })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
          />
          <Button variant="outlined" onClick={load}>
            {t('formations.list.refresh', { defaultValue: 'Refresh' })}
          </Button>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('formations.list.create', { defaultValue: 'New formation' })}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            label={t('formations.fields.code', { defaultValue: 'Code' })}
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            placeholder="TCH-PARTNER-2026"
          />
          <TextField
            size="small"
            label={t('formations.fields.title', { defaultValue: 'Title' })}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={creating || !newCode.trim()}
            onClick={handleCreate}
          >
            {t('formations.list.createButton', { defaultValue: 'Create' })}
          </Button>
        </Box>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.code', { defaultValue: 'Code' })}</TableCell>
                <TableCell sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.title', { defaultValue: 'Title' })}</TableCell>
                <TableCell sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.status', { defaultValue: 'Status' })}</TableCell>
                <TableCell align="right" sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.levels', { defaultValue: 'Levels' })}</TableCell>
                <TableCell align="right" sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>{t('formations.fields.lessons', { defaultValue: 'Lessons' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {programs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary" py={2}>
                      {t('formations.list.empty', { defaultValue: 'No formations yet.' })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                programs.map((p) => (
                  <TableRow
                    key={p._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigateWithLang(`/formations/${p._id}`)}
                  >
                    <TableCell>{p.code}</TableCell>
                    <TableCell>{p.locales?.fr?.title || p.locales?.en?.title || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={p.status} />
                    </TableCell>
                    <TableCell align="right">{p.levelCount}</TableCell>
                    <TableCell align="right">{p.lessonCount}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </ContentPageLayout>
  );
};

export default FormationsListPage;
